import React from 'react';
import { useTranslation } from 'react-i18next';

import type { HomeData } from '../../../../mock-data/types';
import { MetricFrame } from '../MetricFrame';
import { PrimaryStat } from '../PrimaryStat';
import { Stat } from '../Stat';
import { Inner, Divider, SplitRow } from './MoneyFlow.styles';

export type MoneyFlowProps = {
  data: HomeData['moneyFlow'];
  /** Delta caption ("vs yesterday" / "vs last month" / …) — adapts to the range. */
  comparison: string;
  /** While true, the figures show placeholders (the endpoint is loading). */
  loading?: boolean;
  /** Reveal sections whose data isn't available yet (the `show_in_progress` flag). */
  showInProgress?: boolean;
  /** Opens Money Flow in Stats: the whole card becomes a button. */
  onOpen?: () => void;
};

/**
 * "Money Flow" — the Deposits lead figure with Withdrawals / Player Balance split
 * stats. The right rail of the Key-Metrics two-column row (no chart).
 */
export const MoneyFlow = ({
  data,
  comparison,
  loading = false,
  showInProgress = false,
  onOpen,
}: MoneyFlowProps): React.ReactElement => {
  const { t } = useTranslation();
  // Player Balance has no API field yet, so it stays hidden behind the in-progress
  // flag (rather than shipping a bare `?`). It reappears once a field backs it.
  const showPlayerBalance = !data.playerBalance.missing || showInProgress;
  return (
    <MetricFrame title={t('Money Flow')} sub={t('The movement of real cash')} onOpen={onOpen}>
      <Inner>
        <PrimaryStat
          label={t('Deposits')}
          value={data.deposits.value}
          prefix={data.deposits.prefix}
          delta={data.deposits.delta}
          sub={comparison}
          loading={loading}
          error={data.deposits.error}
        />
        <Divider />
        <SplitRow>
          <Stat label={t('Withdrawals')} value={data.withdrawals.value} prefix={data.withdrawals.prefix} suffix={data.withdrawals.suffix} delay={100} compact loading={loading} error={data.withdrawals.error} />
          {showPlayerBalance ? (
            <Stat label={t('Player Balance')} value={data.playerBalance.value} prefix={data.playerBalance.prefix} suffix={data.playerBalance.suffix} delay={200} compact loading={loading} missing={data.playerBalance.missing} error={data.playerBalance.error} />
          ) : null}
        </SplitRow>
      </Inner>
    </MetricFrame>
  );
};

export default MoneyFlow;
